import { useState, useRef } from 'react'
import { Button } from '@components/primitives/Button.js'
import { Modal } from '@components/primitives/Modal.js'
import { Input } from '@components/primitives/Input.js'
import { Select } from '@components/primitives/Select.js'
import { Textarea } from '@components/primitives/Textarea.js'
import { CATEGORIAS_DOCUMENTO, MODULOS } from '@utils/constants.js'
import type { FormState } from './types.js'
import { useDocumentPreviewUrl } from '@hooks/useDocumentPreviewUrl.js'
import {
  formatBytes,
  getFileIcon,
  isImage,
  pickFiles,
  fileToFormPatch,
  revokePreviewObjectUrl,
} from './utils.js'

type Props = {
  open: boolean
  initial: FormState
  title: string
  onSave: (data: FormState) => Promise<void>
  onClose: () => void
  lancamentos: { id: number; titulo: string }[]
}

export function DocumentoFormModal({ open, initial, title, onSave, onClose, lancamentos }: Props) {
  const [form, setForm] = useState<FormState>(initial)
  const [saving, setSaving] = useState(false)
  const [picking, setPicking] = useState(false)
  const fileInputRef = useRef<HTMLInputElement>(null)
  const existingPreview = useDocumentPreviewUrl(
    form.sourceDocId && !form.arquivo
      ? {
          id: form.sourceDocId,
          hasArquivo: form.hasExistingArquivo,
          tipo: form.tipo,
        }
      : null,
  )
  const previewSrc = form.previewObjectUrl || existingPreview

  const set = <K extends keyof FormState>(k: K, v: FormState[K]) =>
    setForm((prev) => ({ ...prev, [k]: v }))

  const handlePickFile = async () => {
    setPicking(true)
    const files = await pickFiles(false)
    if (files.length > 0) {
      const file = files[0]!
      setForm((prev) => {
        if (prev.previewObjectUrl) revokePreviewObjectUrl(prev.previewObjectUrl)
        return { ...prev, ...fileToFormPatch(file) }
      })
    }
    setPicking(false)
  }

  const handleFileInputChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return
    setPicking(true)
    setForm((prev) => {
      if (prev.previewObjectUrl) revokePreviewObjectUrl(prev.previewObjectUrl)
      return { ...prev, ...fileToFormPatch(file) }
    })
    setPicking(false)
  }

  const canSubmit = Boolean(form.nome.trim() && form.categoria)

  const handleSubmit = async () => {
    if (!canSubmit || saving) return
    setSaving(true)
    await onSave(form)
    setSaving(false)
  }

  const catOptions = [
    { value: '', label: 'Selecione…' },
    ...CATEGORIAS_DOCUMENTO.map((c) => ({ value: c, label: c })),
  ]
  const moduloOptions = [
    { value: '', label: 'Nenhum' },
    ...MODULOS.map((m) => ({ value: m.id, label: m.titulo })),
  ]
  const lancOptions = [
    { value: '', label: 'Nenhum' },
    ...lancamentos.map((l) => ({ value: String(l.id), label: l.titulo })),
  ]

  return (
    <Modal
      open={open}
      title={title}
      size='md'
      onClose={onClose}
      actions={
        <div className='cf3-modal-actions'>
          <Button variant='ghost' onClick={onClose} disabled={saving || picking}>
            Cancelar
          </Button>
          <Button
            variant='primary'
            onClick={handleSubmit}
            loading={saving}
            disabled={!canSubmit}
            aria-label='Salvar documento'
          >
            Salvar
          </Button>
        </div>
      }
    >
      <div className='cf3-doc-form'>
        <div className='cf3-doc-file-area'>
          {previewSrc && isImage(form.tipo) ? (
            <img src={previewSrc} alt='Preview' className='cf3-doc-file-area__preview-img' />
          ) : form.nome ? (
            <div className='cf3-doc-file-area__info'>
              <span className='cf3-doc-file-area__icon' aria-hidden>
                {getFileIcon(form.tipo)}
              </span>
              <span className='cf3-doc-file-area__filename'>{form.nome}</span>
              {form.tamanho > 0 && (
                <span className='cf3-doc-file-area__size'>{formatBytes(form.tamanho)}</span>
              )}
            </div>
          ) : (
            <div className='cf3-doc-file-area__placeholder'>
              <span className='cf3-doc-file-area__placeholder-label' aria-hidden>
                ARQ
              </span>
              <p>Nenhum arquivo selecionado</p>
            </div>
          )}
          <Button
            variant='secondary'
            size='sm'
            onClick={handlePickFile}
            loading={picking}
            aria-label='Selecionar arquivo'
          >
            {form.nome ? 'Trocar arquivo' : 'Selecionar arquivo'}
          </Button>
          <input
            ref={fileInputRef}
            type='file'
            accept='image/*,application/pdf,.doc,.docx,.xls,.xlsx,.txt'
            onChange={handleFileInputChange}
            className='cf3-doc-file-area__input'
            tabIndex={-1}
            aria-hidden
          />
        </div>

        <Input
          name='doc-nome'
          label='Nome do documento *'
          value={form.nome}
          placeholder='Ex: Nota fiscal de tijolos'
          onChange={(e) => set('nome', e.target.value)}
        />

        <Select
          name='doc-categoria'
          label='Categoria *'
          value={form.categoria}
          options={catOptions}
          onChange={(e) => set('categoria', e.target.value)}
        />

        <div className='cf3-fin-form__row'>
          <Input
            name='doc-data'
            label='Data do documento'
            type='date'
            value={form.data}
            onChange={(e) => set('data', e.target.value)}
          />
          <Input
            name='doc-valor'
            label='Valor (R$)'
            type='number'
            value={form.valor}
            placeholder='0,00'
            onChange={(e) => set('valor', e.target.value)}
          />
        </div>

        <Input
          name='doc-fornecedor'
          label='Fornecedor / Emissor'
          value={form.fornecedor}
          placeholder='Ex: Depósito São João'
          onChange={(e) => set('fornecedor', e.target.value)}
        />

        <Select
          name='doc-modulo'
          label='Módulo vinculado'
          value={form.moduloId}
          options={moduloOptions}
          onChange={(e) => set('moduloId', e.target.value)}
        />

        {lancamentos.length > 0 && (
          <Select
            name='doc-lancamento'
            label='Lançamento vinculado'
            value={form.lancamentoId}
            options={lancOptions}
            onChange={(e) => set('lancamentoId', e.target.value)}
          />
        )}

        <Textarea
          name='doc-obs'
          label='Observações'
          value={form.observacoes}
          rows={2}
          placeholder='Descrição adicional…'
          onChange={(e) => set('observacoes', e.target.value)}
        />
      </div>
    </Modal>
  )
}
